import {reactive, ref} from 'vue';

const webSocket = ref<WebSocket | null>(null);
const heartbeatMsg = '--heartbeat--';
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
const missedHeartbeats = ref(0);
const missedHeartbeatsLimitMin = 3;
const missedHeartbeatsLimitMax = 50;
const missedHeartbeatsLimit = ref(missedHeartbeatsLimitMin);
const callback = ref<Function | null>(null);
const errorCallback = ref<Function | null>(null);

const signCallback = reactive<{
    callback: Function | null;
    result: any;
}>({
    callback: null,
    result: null
});

const missedHeartbeatsLimit$ = ref(missedHeartbeatsLimit.value);

const init = () => {
    webSocket.value = new WebSocket('wss://127.0.0.1:13579/');

    webSocket.value.onopen = () => {
        if (!heartbeatInterval) {
            missedHeartbeats.value = 0;
            heartbeatInterval = setInterval(() => {
                try {
                    missedHeartbeats.value++;
                    if (missedHeartbeats.value >= missedHeartbeatsLimit.value) {
                        throw new Error('Слишком много пропущенных сигналов.');
                    }
                    webSocket.value?.send(heartbeatMsg);
                } catch (e) {
                    const error = e as Error;
                    clearInterval(heartbeatInterval as ReturnType<typeof setInterval>);
                    heartbeatInterval = null;
                    console.warn('Закрытие соединения. Причина: ' + error.message);
                    webSocket.value?.close();
                }
            }, 2000);
        }
    };

    webSocket.value.onclose = (event: CloseEvent) => {
        if (!event.wasClean) {
            openDialog();
        }
        console.log('Код: ' + event.code + ' Причина: ' + event.reason);
    };

    webSocket.value.onmessage = (event: MessageEvent) => {
        if (event.data.body === undefined || event.data.body === null) {
            setSignCallback({callback: errorCallback.value, result: null});
        }
        if (event.data === heartbeatMsg) {
            missedHeartbeats.value = 0;
            return;
        }

        const data = JSON.parse(event.data);
        const {body, status} = data;
        if (data && callback.value) {
            const rw = {
                status,
                result: extractBase64(body.result)
            };
            setSignCallback({callback: callback.value, result: rw});
        }
        setMissedHeartbeatsLimitToMin();
    };
};

const setMissedHeartbeatsLimitToMax = () => {
    missedHeartbeatsLimit.value = missedHeartbeatsLimitMax;
    missedHeartbeatsLimit$.value = missedHeartbeatsLimit.value;
};

const setMissedHeartbeatsLimitToMin = () => {
    missedHeartbeatsLimit.value = missedHeartbeatsLimitMin;
    missedHeartbeatsLimit$.value = missedHeartbeatsLimit.value;
};

const openDialog = () => {
    if (confirm('Ошибка при подключении. Убедитесь, что программа NCALayer запущена, и нажмите ОК')) {
        location.reload();
    }
};

const getActiveTokens = (cb: Function) => {
    const getActiveTokens = {
        module: 'kz.gov.pki.knca.commonUtils',
        method: 'getActiveTokens'
    };
    callback.value = cb;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(getActiveTokens));
};

const getKeyInfo = (storageName: string, cb: Function) => {
    const getKeyInfo = {
        module: 'kz.gov.pki.knca.commonUtils',
        method: 'getKeyInfo',
        args: [storageName]
    };
    callback.value = cb;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(getKeyInfo));
};

const signXml = (storageName: string, keyType: string, xmlToSign: string, cb: Function) => {
    const signXml = {
        module: 'kz.gov.pki.knca.commonUtils',
        method: 'signXml',
        args: [storageName, keyType, xmlToSign, '', '']
    };

    callback.value = cb;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(signXml));
};

const createCMSSignatureFromFile = (storageName: string, keyType: string, filePath: string, flag: boolean, cb: Function) => {
    const createCMSSignatureFromFile = {
        module: 'kz.gov.pki.knca.commonUtils',
        method: 'createCMSSignatureFromFile',
        args: [storageName, keyType, filePath, flag]
    };
    callback.value = cb;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(createCMSSignatureFromFile));
};

const createCMSSignatureFromBase64 = (storageName: string, keyType: string, base64ToSign: string, flag: boolean, cb: Function) => {
    const createCMSSignatureFromBase64 = {
        module: "kz.gov.pki.knca.basics",
        method: "sign",
        args: {
            format: "cms",
            locale: "ru",
            data: base64ToSign,
            signerParams: {
                bin: "",
                chain: null,
                extKeyUsageOids: ["1.3.6.1.5.5.7.3.4"],
                iin: "",
                serialNumber: "",
            },
            signingParams: {
                decode: true,
                encapsulate: false,
                digested: false,
                tsaProfile: null,
            },
        },
    };

    callback.value = cb;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(createCMSSignatureFromBase64));
}

const encodeToBase64 = (input: string): string => {
    return btoa(input);
};

const createCMSSignatureForAuthenticate = (cb: Function, cbError: Function) => {
    console.log('encode', encodeToBase64("fincenter_authentication"))
    const createCMSSignatureForAuthenticate = {
        module: "kz.gov.pki.knca.basics",
        method: "sign",
        args: {
            format: "cms",
            locale: "ru",
            data: encodeToBase64("fincenter_authentication"),
            signerParams: {
                bin: "",
                chain: null,
                extKeyUsageOids: ["1.3.6.1.5.5.7.3.4"],
                iin: "",
                serialNumber: "",
            },
            signingParams: {
                decode: true,
                encapsulate: false,
                digested: false,
                tsaProfile: null,
            },
        },
    };

    callback.value = cb;
    errorCallback.value = cbError;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(createCMSSignatureForAuthenticate));
}

const showFileChooser = (fileExtension: string, currentDirectory: string, cb: Function) => {
    const showFileChooser = {
        module: 'kz.gov.pki.knca.commonUtils',
        method: 'showFileChooser',
        args: [fileExtension, currentDirectory]
    };
    callback.value = cb;
    setMissedHeartbeatsLimitToMax();
    webSocket.value?.send(JSON.stringify(showFileChooser));
};

const getDsInfoProperty = (dsInfo: string, prop: string) => {
    const arr = dsInfo.split(',');
    let value = '';
    arr.forEach((element: string) => {
        if (element.includes(`${prop}=`)) {
            const strs = element.split('=');
            if (strs && strs.length > 1) {
                value = strs[1];
            }
        }
    });
    return value;
};

const setSignCallback = (value: { callback: Function | null; result: any }) => {
    signCallback.callback = value.callback;
    signCallback.result = value.result;
};

const extractBase64 = (cmsString: string | string[]): string => {
    let base64: string;

    if (typeof cmsString === "string") {
        base64 = cmsString
            .replace(/-----BEGIN CMS-----/, "")
            .replace(/-----END CMS-----/, "");
    } else if (Array.isArray(cmsString) && typeof cmsString[0] === "string") {
        base64 = cmsString[0]
            .replace(/-----BEGIN CMS-----/, "")
            .replace(/-----END CMS-----/, "");
    } else {
        throw new Error(
            "Invalid input: cmsString must be a string or an array of strings"
        );
    }

    return base64.replace(/\r?\n|\r/g, "");
}

export default {
    init,
    getActiveTokens,
    getKeyInfo,
    signXml,
    createCMSSignatureFromFile,
    createCMSSignatureFromBase64,
    createCMSSignatureForAuthenticate,
    showFileChooser,
    getDsInfoProperty,
    signCallback,
    missedHeartbeatsLimit$
};
